import React, { useEffect, useState } from "react";
import {
  Row,
  Column,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  DataTable,
  Button,
  TableToolbar,
  TableToolbarContent,
  Tooltip,
} from "carbon-components-react";
import { useSelector } from "react-redux";
import confirm from "../../forms/confirm";
import { Edit16, TrashCan16 } from "@carbon/icons-react";
import Card from "../../components/card";
import DescriptionDetails from "../../components/description-details";
import { maskSSN } from "../../components/formatters";
import { BeneficiaryType } from "../../components/enums/beneficiary-type";
import { useFormContext } from "react-hook-form";
import { buildBeneficiaries } from "../../components/contact-builder";

const Beneficiaries = ({
  beneficiaries,
  setBeneficiaries,
  beneficiary,
  setBeneficiary,
  show,
  setShow,
  type,
  reset,
  setIsEmployee
}) => {
  const enrollments = useSelector((s) => s.enrollments);
  const [loading, setLoading] = useState(true);
  const { formState, trigger } = useFormContext();

  const headers = [
    { key: "name", header: "Name" },
    { key: "relationship", header: "Relationship" },
    { key: "ssn", header: "SSN" },
    { key: "address", header: "Address" },
    { key: "percentOfBenefit", header: "% of Benefit" },
    { key: "type", header: "Type" },
  ];

  useEffect(() => {
    if (Array.isArray(enrollments)) return;
    setBeneficiaries(buildBeneficiaries(enrollments));
    setLoading(false);
    setTimeout(() => {
      trigger("primaryPercentage");
      trigger("contingentPercentage");
    }, 100);
  }, [enrollments, reset]);

  useEffect(() => {
    if (show === false) {
      trigger("primaryPercentage");
      trigger("contingentPercentage");
    }
  }, [show]);

  const buildAddress = (address) => {
    return (
      <>
        {address?.addressLine1 ? (
          <span>
            {address.addressLine1}
            <br />
            {address.addressLine2 ? (
              <>
                {address?.addressLine2} <br />
              </>
            ) : (
              <></>
            )}
            {address.city}, {address.state ?? address.region} {address.postalCode}
            {address.country !== "US" ?
            <>
              <br />{address.country}
            </> : <></>
            }
          </span>
        ) : (
          <></>
        )}
      </>
    );
  };

  const confirmDelete = async (person) => {
    const result = await confirm(
      `Are you sure you want to remove ${person.firstName} ${person.lastName} as a dependent?`,
      { title: "Remove Dependent", buttonText: "REMOVE" }
    );
    if (result) {
      setBeneficiaries(beneficiaries.filter((s) => s.id !== person.id));
      setTimeout(() => {
        trigger("primaryPercentage");
        trigger("contingentPercentage");
      }, 100);
    }
  };

  const links = (currentDependent) => {
    return (
      <>
        <Button
          hasIconOnly
          iconDescription={`Edit ${currentDependent.firstName}`}
          kind="ghost"
          size="sm"
          onClick={() => {
            setBeneficiary(currentDependent);
            if(currentDependent.relationship?.name === "Employee"){
            setIsEmployee(true);
            }
            setShow(true);
          }}
        >
          <Edit16 />
        </Button>
        <Button
          hasIconOnly
          iconDescription={`Remove ${currentDependent.firstName}`}
          kind="ghost"
          size="sm"
          onClick={() => {
            confirmDelete(currentDependent);
          }}
        >
          <TrashCan16 />
        </Button>
      </>
    );
  };

  return (
    <div>
      <Row>
        <Column sm={{ span: 0 }} md={{ span: 8 }} className="p-0">
          <DataTable headers={headers} rows={[]}>
            {() => (
              <TableContainer>
                <TableToolbar>
                  <h4 className="my-2 pl-2 whitespace-nowrap">
                    {type === BeneficiaryType.Primary ? (
                      <span>
                        Primary
                        <Tooltip>
                          A primary beneficiary is the person (or persons) who
                          will receive the benefit from your life insurance
                          policy
                        </Tooltip>
                      </span>
                    ) : (
                      <span>
                        Contingent
                        <Tooltip>
                          A contingent beneficiary is a beneficiary(ies) who
                          will receive the benefits if the primary
                          beneficiary(ies) are also deceased at the time the
                          benefit is to be paid
                        </Tooltip>
                      </span>
                    )}
                  </h4>
                </TableToolbar>

                <Table>
                  <TableHead>
                    <TableRow>
                      {headers.map((header, idx) => (
                        <TableHeader key={idx} className="p-4">
                          {header.header}
                        </TableHeader>
                      ))}
                      <TableHeader></TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {beneficiaries
                      .filter((s) => s.type === type)
                      ?.map((beneficiary, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="p-4">
                            {beneficiary.firstName + " " + beneficiary.lastName}
                          </TableCell>
                          <TableCell className="p-4">
                            {beneficiary.relationship?.name}
                          </TableCell>
                          <TableCell className="p-4">
                          {beneficiary.isEmployee === true ?
                            <>Not Shown</> : <>{beneficiary.ssn}</>
                          }
                          </TableCell>
                          <TableCell className="p-4">
                          {beneficiary.isEmployee === true ?
                            <>Not Shown</> : <>{buildAddress(beneficiary.address)}</>
                          }
                          </TableCell>
                          <TableCell className="p-4">
                            {beneficiary.percentage + "%"}
                          </TableCell>
                          <TableCell className="p-4">
                            {beneficiary.type === 1 ? "Primary" : "Contingent"}
                          </TableCell>
                          <TableCell>{links(beneficiary)} </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                {beneficiaries.filter((s) => s.type === type)?.length === 0 && (
                  <h6 className="pt-2 pl-4 py-6">
                    No 
                    {type === BeneficiaryType.Primary ? <> primary </> : <> contingent </> }
                     beneficiaries have been added
                  </h6>
                )}
              </TableContainer>
            )}
          </DataTable>
        </Column>
        <Column md={{ span: 0 }}>
          <DataTable headers={headers} rows={[]}>
            {() => (
              <TableContainer>
                <TableToolbar>
                  <h4 className="my-2 pl-2 whitespace-nowrap">
                    {type === BeneficiaryType.Primary ? (
                      <span>
                        Primary
                        <Tooltip>
                          A primary beneficiary is the person (or persons) who
                          will receive the benefit from your life insurance
                          policy
                        </Tooltip>
                      </span>
                    ) : (
                      <span>
                        Contingent
                        <Tooltip>
                          A contingent beneficiary is a beneficiary(ies) who
                          will receive the benefits if the primary
                          beneficiary(ies) are also deceased at the time the
                          benefit is to be paid
                        </Tooltip>
                      </span>
                    )}
                  </h4>
                </TableToolbar>
              </TableContainer>
            )}
          </DataTable>
          {beneficiaries
            .filter((s) => s.type === type)
            ?.map((beneficiary) => (
              <Card
                key={beneficiary.id}
                title={
                  <div className="grid grid-cols-2 gap-4 place-items-center">
                    <div className="justify-self-start">{`${beneficiary.firstName} ${beneficiary.lastName}`}</div>
                    <span className="justify-self-end">
                      {links(beneficiary)}
                    </span>
                  </div>
                }
              >
                <DescriptionDetails
                  term="Relationship"
                  definition={beneficiary.relationship?.name}
                />
                <DescriptionDetails
                  term="Social Security Number"
                  definition={maskSSN(beneficiary.ssn)}
                />
                <DescriptionDetails
                  term="Address"
                  definition={buildAddress(beneficiary.address)}
                />
                <DescriptionDetails
                  term="Benefit %"
                  definition={`${beneficiary.percentage}%`}
                />
                <DescriptionDetails
                  term="Type"
                  definition={beneficiary.type === 1 ? "Primary" : "Contingent"}
                />
              </Card>
            ))}
            {beneficiaries.filter((s) => s.type === type)?.length === 0 && (
                  <h6 className="pt-2 pl-4 py-6">
                    No 
                    {type === BeneficiaryType.Primary ? <> primary </> : <> contingent </> }
                     beneficiaries have been added
                  </h6>
                )}
        </Column>
        <span className="text-danger text-sm pl-4 font-semibold">
          {
            formState.errors[
              type === BeneficiaryType.Primary
                ? "primaryPercentage"
                : "contingentPercentage"
            ]?.message
          }
        </span>
      </Row>
    </div>
  );
};

export default Beneficiaries;
